import { Button, ButtonVariants } from '@triumph/design-system';
import { Checkbox } from 'primereact/checkbox';
import { Dropdown } from 'primereact/dropdown';
import { MultiSelect } from 'primereact/multiselect';
import { useMemo, useState } from 'react';

import { ProductNames } from '@/components/providers/ApplicantContextProvider/ApplicantContextProvider.types';
import { CONDITION_DEFINITION_PRODUCT_NAME } from '@/components/shared/api/endpoints/conditionDefinitions';
import {
    useAddConditionMutation,
    useEditConditionMutation,
} from '@/components/shared/api/mutations/conditions';
import { useConditionDefinitionsQuery } from '@/components/shared/api/queries/conditionDefinitions';
import { CheckboxLabel } from '@/components/shared/components/common.StyledComponents';
import {
    MentionTextarea,
    useMentionSuggestions,
} from '@/components/shared/components/MentionTextarea';
import { useFeatureFlags } from '@/components/shared/constants/featureFlags';

import {
    AppliesToMultiSelectWrapper,
    ErrorText,
    FormActions,
    FormLabel,
    ModalField,
    TemplateOption,
} from './ApplicantConditions.StyledComponents';
import { Condition } from './ApplicantConditions.types';
import { getAppliesToProductOptions } from './ApplicantConditions.utils';
import {
    ASSIGNABLE_REVIEW_TEAMS,
    getReviewTeams,
    REVIEW_TEAM_LABEL,
    ReviewTeam,
} from './conditionTemplates';
import { ReviewTeamPill } from './ReviewTeamPill';
import { ReviewTeamPills } from './ReviewTeamPills';

interface FieldErrors {
    description?: string;
    appliesTo?: string;
    team?: string;
    submit?: string;
}

interface Props {
    applicantId: string;
    availableProducts: ProductNames[];
    /** Initial "Applies to" selection. Defaults to the first available product. */
    defaultProducts?: ProductNames[];
    /** Label for the description field. Defaults to "Task/Condition". */
    descriptionLabel?: string;
    /** Label for the save button. Defaults to "Save Task/Condition" (or "Add Follow-Up Task/Condition" in follow-up mode). */
    saveLabel?: string;
    /** Label for the cancel button. Defaults to "Cancel". */
    cancelLabel?: string;
    /**
     * Horizontal alignment of the action buttons. Defaults to right-aligned when
     * a Cancel button is shown (dialog style), left-aligned otherwise.
     */
    actionsAlign?: 'left' | 'right';
    /** Cross-links the new condition back to the Problematic condition that spawned it. */
    parentConditionId?: string;
    /**
     * When provided, the form edits this existing condition in place instead of creating
     * one — pre-filled with its text/team/products. The template picker is disabled and
     * the watch-on-create checkbox is hidden, since neither applies to an edit.
     */
    editCondition?: Condition;
    /** Called with the saved text after the condition is successfully added (or, in edit mode, saved). */
    onAdded?: (description: string) => void;
    /**
     * When provided, a Cancel button is rendered alongside Add. Omit for an
     * Add-only action slot.
     */
    onCancel?: () => void;
}

export const AddConditionForm = ({
    applicantId,
    availableProducts,
    defaultProducts,
    descriptionLabel = 'Task/Condition',
    saveLabel,
    cancelLabel = 'Cancel',
    actionsAlign,
    parentConditionId,
    editCondition,
    onAdded,
    onCancel,
}: Props) => {
    const options = useMemo(
        () => getAppliesToProductOptions(availableProducts),
        [availableProducts],
    );
    const fallbackDefault = options[0]?.value;
    const initialProducts = useMemo(
        () => defaultProducts ?? (fallbackDefault ? [fallbackDefault] : []),
        [defaultProducts, fallbackDefault],
    );

    const { isConditionReviewTeamEnabled, isConditionsWatcherEnabled } = useFeatureFlags();

    const isEditMode = Boolean(editCondition);
    const isFollowUpMode = Boolean(parentConditionId);
    const [description, setDescription] = useState(editCondition?.description ?? '');
    const [appliesTo, setAppliesTo] = useState<ProductNames[]>(
        editCondition?.products ?? initialProducts,
    );
    const [templateId, setTemplateId] = useState<string | null>(null);
    const [teams, setTeams] = useState<ReviewTeam[]>(() =>
        getReviewTeams(editCondition?.reviewTeams),
    );
    const [errors, setErrors] = useState<FieldErrors>({});
    const [submitting, setSubmitting] = useState(false);
    const [watchCreatedConditions, setWatchCreatedConditions] = useState(false);

    const clearError = (field: keyof FieldErrors) =>
        setErrors((prev) => {
            if (!prev[field]) return prev;
            const next = { ...prev };
            delete next[field];
            return next;
        });

    const {
        data: conditionDefinitions,
        isLoading: isLoadingTemplates,
        isError: isTemplatesError,
    } = useConditionDefinitionsQuery();

    // Coded rows are created automatically by the backend, so only manual templates are offered here.
    const templates = useMemo(
        () =>
            (conditionDefinitions ?? [])
                .filter((d) => d.isActive && d.code === null)
                .sort((a, b) => a.label.localeCompare(b.label)),
        [conditionDefinitions],
    );

    const templateOptions = useMemo(
        () =>
            templates.map((t) => {
                const templateTeams = getReviewTeams(t.reviewTeams);
                return {
                    label: t.label,
                    value: t.id,
                    teams: templateTeams,
                    teamLabel: templateTeams.map((team) => REVIEW_TEAM_LABEL[team]).join(' '),
                };
            }),
        [templates],
    );

    const templatePlaceholder = isLoadingTemplates
        ? 'Loading templates…'
        : isTemplatesError
          ? 'Templates unavailable'
          : 'Search templates…';

    const teamOptions = useMemo(
        () =>
            ASSIGNABLE_REVIEW_TEAMS.map((team) => ({
                label: REVIEW_TEAM_LABEL[team],
                value: team,
            })),
        [],
    );

    const handleTemplateChange = (nextId: string | null) => {
        const selected = nextId ? templates.find((t) => t.id === nextId) : null;
        const availableSet = new Set(availableProducts);
        const templateProducts = (selected?.defaultProducts ?? [])
            .map((p) => CONDITION_DEFINITION_PRODUCT_NAME[p])
            .filter((p): p is ProductNames => p !== null && availableSet.has(p));
        // A template without a team leaves the reviewer's current picks alone rather than clearing them.
        const templateTeams = getReviewTeams(selected?.reviewTeams);
        const prefillsTeam = templateTeams.length > 0 && isConditionReviewTeamEnabled;

        setTemplateId(nextId);
        const nextText = selected?.descriptionTemplate ?? '';
        setDescription(nextText);
        if (templateProducts.length > 0) setAppliesTo(templateProducts);
        if (prefillsTeam) setTeams(templateTeams);
        setErrors((prev) => {
            const next = { ...prev };
            if (nextText) delete next.description;
            if (templateProducts.length > 0) delete next.appliesTo;
            if (prefillsTeam) delete next.team;
            return next;
        });
    };

    const mentionSuggestions = useMentionSuggestions(applicantId);

    const { mutateAsync: addCondition } = useAddConditionMutation();
    const { mutateAsync: saveConditionEdit } = useEditConditionMutation();

    const validate = (): FieldErrors => {
        const next: FieldErrors = {};
        if (!description.trim()) next.description = 'Task/condition text is required.';
        if (appliesTo.length === 0) next.appliesTo = 'Select at least one product this applies to.';
        if (isConditionReviewTeamEnabled && teams.length === 0 && !isEditMode)
            next.team = 'Select at least one review team.';
        return next;
    };

    const handleSave = async () => {
        const validationErrors = validate();
        if (validationErrors.description || validationErrors.appliesTo || validationErrors.team) {
            setErrors(validationErrors);
            return;
        }

        setSubmitting(true);
        try {
            if (editCondition) {
                await saveConditionEdit({
                    applicantId,
                    conditionId: editCondition.id,
                    description: description.trim(),
                    products: appliesTo,
                    teams: isConditionReviewTeamEnabled
                        ? teams
                        : getReviewTeams(editCondition.reviewTeams),
                });
            } else {
                await addCondition({
                    applicantId,
                    products: appliesTo,
                    conditions: [description.trim()],
                    teams: isConditionReviewTeamEnabled && teams.length > 0 ? teams : undefined,
                    parentConditionId,
                    hasOptedIntoWatcher: isConditionsWatcherEnabled
                        ? watchCreatedConditions
                        : undefined,
                });
            }
            setSubmitting(false);
            onAdded?.(description.trim());
        } catch {
            setSubmitting(false);
            setErrors((prev) => ({
                ...prev,
                submit: isEditMode
                    ? 'Failed to save changes. Please try again.'
                    : 'Failed to add condition. Please try again.',
            }));
        }
    };

    const defaultSaveLabel = isEditMode
        ? 'Save Changes'
        : isFollowUpMode
          ? 'Save Follow-Up Task/Condition'
          : 'Save Task/Condition';
    const resolvedSaveLabel = saveLabel ?? defaultSaveLabel;

    return (
        <>
            <div
                data-testid="add-condition-row-0"
                style={{ display: 'flex', flexDirection: 'column', gap: 16 }}
            >
                {isEditMode ? null : (
                    <ModalField>
                        <FormLabel htmlFor="add-condition-template">Template (optional)</FormLabel>
                        <AppliesToMultiSelectWrapper>
                            <Dropdown
                                inputId="add-condition-template"
                                data-testid="add-condition-template"
                                value={templateId}
                                options={templateOptions}
                                onChange={(e) => handleTemplateChange(e.value ?? null)}
                                placeholder={templatePlaceholder}
                                disabled={isLoadingTemplates || isTemplatesError}
                                filter
                                filterBy="label,teamLabel"
                                filterPlaceholder="Search by name or team…"
                                showClear
                                appendTo="self"
                                itemTemplate={(option) => (
                                    <TemplateOption>
                                        <span>{option.label}</span>
                                        <ReviewTeamPills teams={option.teams} isCompact={true} />
                                    </TemplateOption>
                                )}
                            />
                        </AppliesToMultiSelectWrapper>
                    </ModalField>
                )}
                <ModalField>
                    <FormLabel htmlFor="add-condition-description">{descriptionLabel}</FormLabel>
                    <MentionTextarea
                        spellCheck
                        id="add-condition-description"
                        data-testid="add-condition-description"
                        placeholder="Describe the task/condition…"
                        value={description}
                        onChange={(val) => {
                            setDescription(val);
                            if (val.trim()) clearError('description');
                        }}
                        hasError={Boolean(errors.description)}
                        suggestions={mentionSuggestions}
                    />
                    {errors.description ? <ErrorText>{errors.description}</ErrorText> : null}
                </ModalField>
                {isConditionReviewTeamEnabled ? (
                    <ModalField>
                        <FormLabel htmlFor="add-condition-team">Review Team</FormLabel>
                        <AppliesToMultiSelectWrapper>
                            <MultiSelect
                                inputId="add-condition-team"
                                data-testid="add-condition-team"
                                value={teams}
                                options={teamOptions}
                                onChange={(e) => {
                                    const nextTeams = getReviewTeams(e.value as ReviewTeam[]);
                                    setTeams(nextTeams);
                                    if (nextTeams.length > 0) clearError('team');
                                }}
                                placeholder={isEditMode ? 'No team' : 'Select teams…'}
                                showSelectAll={false}
                                showClear={isEditMode}
                                appendTo={document.body}
                                className={errors.team ? 'p-invalid' : undefined}
                                selectedItemTemplate={(value?: ReviewTeam) =>
                                    value != null ? (
                                        <span data-testid="add-condition-team-value">
                                            <ReviewTeamPill team={value} isCompact={true} />
                                        </span>
                                    ) : (
                                        <span>{isEditMode ? 'No team' : 'Select teams…'}</span>
                                    )
                                }
                                itemTemplate={(option: { value: ReviewTeam }) => (
                                    <ReviewTeamPill team={option.value} isCompact={true} />
                                )}
                            />
                        </AppliesToMultiSelectWrapper>
                        {errors.team ? <ErrorText>{errors.team}</ErrorText> : null}
                    </ModalField>
                ) : null}
                <ModalField>
                    <FormLabel htmlFor="add-condition-applies-to">Applies to</FormLabel>
                    <AppliesToMultiSelectWrapper>
                        <MultiSelect
                            inputId="add-condition-applies-to"
                            data-testid="add-condition-applies-to"
                            value={appliesTo}
                            options={options}
                            onChange={(e) => {
                                setAppliesTo(e.value);
                                if (e.value.length > 0) clearError('appliesTo');
                            }}
                            display="chip"
                            placeholder="Select product(s)"
                            showSelectAll={false}
                            showClear={false}
                            disabled={options.length <= 1}
                            className={errors.appliesTo ? 'p-invalid' : undefined}
                        />
                    </AppliesToMultiSelectWrapper>
                    {errors.appliesTo ? <ErrorText>{errors.appliesTo}</ErrorText> : null}
                </ModalField>
                {errors.submit ? <ErrorText>{errors.submit}</ErrorText> : null}
            </div>
            {isConditionsWatcherEnabled && !isEditMode ? (
                <ModalField>
                    <CheckboxLabel htmlFor="add-condition-watch">
                        <Checkbox
                            inputId="add-condition-watch"
                            data-testid="add-condition-watch"
                            checked={watchCreatedConditions}
                            onChange={(e) => setWatchCreatedConditions(Boolean(e.checked))}
                        />
                        Watch this task/condition
                    </CheckboxLabel>
                </ModalField>
            ) : null}
            <FormActions
                style={{
                    justifyContent:
                        (actionsAlign ?? (onCancel ? 'right' : 'left')) === 'right'
                            ? 'flex-end'
                            : 'flex-start',
                }}
            >
                {onCancel ? (
                    <Button
                        variant={ButtonVariants.secondary}
                        onClick={onCancel}
                        disabled={submitting}
                    >
                        {cancelLabel}
                    </Button>
                ) : null}
                <Button
                    variant={ButtonVariants.primary}
                    onClick={handleSave}
                    loading={submitting}
                    disabled={submitting}
                    data-testid="add-condition-save"
                >
                    {resolvedSaveLabel}
                </Button>
            </FormActions>
        </>
    );
};
